'use client';

import { useCallback, useEffect, useState } from 'react';
import { FiLock } from 'react-icons/fi';
import { useAuth } from '@/contexts/AuthContext';
import LoginRequiredModal from '@/components/LoginRequiredModal';
import { useRazorpayCheckout } from '@/lib/hooks/useRazorpayCheckout';

interface QuotaInfo {
  used: number;
  limit: number;
  remaining: number;
  isExceeded: boolean;
}

interface Plan {
  id: number;
  name: string;
  price: number;
  currency: string;
  is_active?: boolean;
  contact_quota?: number;
  maps_quota?: number;
}

interface UnlockPaywallProps {
  /** Which gate the user hit — only changes the wording. */
  kind: 'contacts' | 'maps';
  quotaInfo?: QuotaInfo | null;
  /** Runs after the server has verified the payment. Re-check access here. */
  onUnlocked: () => void | Promise<void>;
  onDismiss: () => void;
  dismissLabel?: string;
  /** Shown while onUnlocked is running. */
  unlockingLabel?: string;
  /**
   * The contact behind this gate is known not to answer. Says so plainly above the
   * pay button — the risk stays with the user, so it has to be legible before they pay.
   */
  unreachableWarning?: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
const currencySymbol = (code: string) => CURRENCY_SYMBOLS[String(code || '').toUpperCase()] ?? code;
const formatAmount = (value: number) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const COPY = {
  contacts: {
    lockedTitle: 'Unlock contact details',
    lockedTitleHi: 'संपर्क जानकारी अनलॉक करें',
    usedUpTitle: 'You’ve used all your contacts',
    usedUpTitleHi: 'आपके सभी संपर्क इस्तेमाल हो गए',
    pitch: 'to see teacher phone numbers and school maps.',
    pitchHi: 'देकर शिक्षकों के नंबर और स्कूल मैप देखें।',
    unit: 'used',
  },
  maps: {
    lockedTitle: 'Unlock school maps',
    lockedTitleHi: 'स्कूल मैप अनलॉक करें',
    usedUpTitle: 'You’ve used all your map views',
    usedUpTitleHi: 'आपके सभी मैप व्यू इस्तेमाल हो गए',
    pitch: 'to open school maps and teacher contacts.',
    pitchHi: 'देकर स्कूल मैप और शिक्षकों के संपर्क खोलें।',
    unit: 'used',
  },
};

/**
 * The paywall both quota gates share: pay here, stay where you are. Shows a
 * selector when more than one plan is active (cheapest pre-selected); falls
 * back to the pricing page only when no plan is purchasable.
 */
export default function UnlockPaywall({
  kind,
  quotaInfo,
  onUnlocked,
  onDismiss,
  dismissLabel = 'Not now',
  unlockingLabel = 'Opening your access…',
  unreachableWarning = false,
}: UnlockPaywallProps) {
  const { user } = useAuth();
  const { pay, isPaying, isReady } = useRazorpayCheckout();
  const [showLogin, setShowLogin] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/plans?tool=bihar');
        const data = await res.json();
        const active: Plan[] = (data?.data?.plans ?? [])
          .filter((p: Plan) => p.is_active !== false && Number(p.price) > 0)
          .sort((a: Plan, b: Plan) => Number(a.price) - Number(b.price));
        if (!cancelled) {
          setPlans(active);
          setSelectedId(active[0]?.id ?? null);
        }
      } catch {
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setPlanLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const plan = plans.find((p) => p.id === selectedId) ?? null;

  const handlePay = useCallback(() => {
    if (!plan) return;
    setPayError(null);
    pay({
      planId: plan.id,
      amount: plan.price,
      planName: plan.name,
      onPaid: async () => {
        setUnlocking(true);
        try {
          await onUnlocked();
        } finally {
          setUnlocking(false);
        }
      },
      onError: (error) => setPayError(error.message),
    });
  }, [onUnlocked, pay, plan]);

  const contactsGranted = Number(plan?.contact_quota) || 0;
  const mapsGranted = Number(plan?.maps_quota) || 0;
  /** Quota stacks per payment, so for an existing subscriber this is "more on top". */
  const isTopUp = !!user && !!quotaInfo && quotaInfo.limit > 0;

  const grants: { en: string; hi: string }[] = [];
  if (contactsGranted > 0) {
    grants.push({
      en: `${contactsGranted} contact number${contactsGranted === 1 ? '' : 's'}`,
      hi: `${contactsGranted} संपर्क नंबर`,
    });
  }
  if (mapsGranted > 0) {
    grants.push({
      en: `${mapsGranted} school map view${mapsGranted === 1 ? '' : 's'}`,
      hi: `${mapsGranted} स्कूल मैप व्यू`,
    });
  }

  const copy = COPY[kind];
  // A signed-out visitor has nothing used up yet — they're simply locked out.
  const hasNoPlan = !user || (!!quotaInfo && quotaInfo.limit === 0);
  const symbol = plan ? currencySymbol(plan.currency) : '₹';
  const amount = plan ? formatAmount(plan.price) : '';
  const busy = unlocking || isPaying;

  return (
    <div>
      <div className="bg-[#101B2D] px-5 pb-4 pt-5">
        <div className="flex items-center gap-2 text-[#F0A020]">
          <FiLock size={14} />
          <p className="font-mono text-[10px] uppercase tracking-[0.22em]">
            {hasNoPlan ? 'Locked' : 'Limit reached'}
          </p>
        </div>
        <h3 className="mt-2 text-lg font-semibold leading-tight text-white">
          {hasNoPlan ? copy.lockedTitle : copy.usedUpTitle}
        </h3>
        <p lang="hi" className="mt-1 text-sm text-white/55">
          {hasNoPlan ? copy.lockedTitleHi : copy.usedUpTitleHi}
        </p>
        {user && quotaInfo && quotaInfo.limit > 0 && (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
            {quotaInfo.used} / {quotaInfo.limit} {copy.unit}
          </p>
        )}
      </div>

      <div className="p-5">
        {plan ? (
          <>
            {plans.length > 1 && (
              <div className="mb-3 grid grid-cols-2 gap-2">
                {plans.map((p) => {
                  const selected = p.id === plan.id;
                  const c = Number(p.contact_quota) || 0;
                  const m = Number(p.maps_quota) || 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      className={`rounded-xl p-3 text-left transition-colors ${
                        selected
                          ? 'bg-[#F0A020]/10 ring-2 ring-[#F0A020]'
                          : 'bg-[#101B2D]/[0.04] ring-1 ring-[#101B2D]/10 hover:ring-[#101B2D]/25'
                      }`}
                    >
                      <span className="block text-base font-bold leading-none text-[#101B2D]">
                        {currencySymbol(p.currency)}{formatAmount(p.price)}
                      </span>
                      <span lang="hi" className="mt-1 block text-[11px] leading-tight text-[#101B2D]/60">
                        {c} संपर्क · {m} मैप
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-sm text-[#101B2D]/75">
              Pay <span className="font-semibold text-[#101B2D]">{symbol}{amount}</span> once {copy.pitch}
            </p>
            <p lang="hi" className="mt-1 text-sm text-[#101B2D]/50">
              एक बार {symbol}{amount} {copy.pitchHi}
            </p>

            {/* With the selector visible each card already lists its quotas —
                repeating them here just makes the popup scroll on small phones. */}
            {plans.length === 1 && grants.length > 0 && (
              <div className="mt-3 rounded-xl bg-[#101B2D]/[0.04] p-3 ring-1 ring-[#101B2D]/10">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#101B2D]/55">
                  {isTopUp ? 'Adds to your account' : 'You get'}
                  <span lang="hi" className="ml-1 font-sans normal-case tracking-normal text-[#101B2D]/40">
                    · {isTopUp ? 'आपके खाते में जुड़ेगा' : 'आपको मिलेगा'}
                  </span>
                </p>
                <ul className="mt-2 space-y-1.5">
                  {grants.map((g, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="font-mono text-xs font-semibold text-[#F0A020]">+</span>
                      <span className="text-sm font-semibold leading-tight text-[#101B2D]">
                        {g.en}
                        <span lang="hi" className="ml-1.5 text-xs font-normal text-[#101B2D]/50">{g.hi}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {unreachableWarning && (
              <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 ring-1 ring-amber-300">
                <p className="text-xs font-semibold leading-tight text-amber-900">
                  This number has never replied to us. Unlock at your own risk.
                </p>
                <p lang="hi" className="mt-1 text-xs leading-tight text-amber-800/80">
                  इस नंबर ने हमें कभी जवाब नहीं दिया — अपने जोखिम पर अनलॉक करें।
                </p>
              </div>
            )}

            {payError && (
              <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{payError}</p>
            )}

            <button
              onClick={user ? handlePay : () => setShowLogin(true)}
              disabled={user ? busy || !isReady : false}
              className="mt-4 flex min-h-[3.25rem] w-full flex-col items-center justify-center rounded-xl bg-[#F0A020] px-4 py-2 leading-tight text-[#101B2D] shadow-[0_10px_22px_-12px_rgba(240,160,32,0.9)] transition-colors hover:bg-[#DE9012] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {!user ? (
                <>
                  <span className="text-sm font-bold tracking-tight">Log in to continue</span>
                  <span lang="hi" className="mt-0.5 text-[11px] font-medium text-[#101B2D]/65">
                    जारी रखने के लिए लॉग इन करें
                  </span>
                </>
              ) : unlocking ? (
                <span className="text-sm font-bold">{unlockingLabel}</span>
              ) : isPaying ? (
                <span className="text-sm font-bold">Waiting for payment…</span>
              ) : (
                <>
                  <span className="text-sm font-bold tracking-tight">Pay {symbol}{amount} · Unlock now</span>
                  <span lang="hi" className="mt-0.5 text-[11px] font-medium text-[#101B2D]/65">
                    {symbol}{amount} देकर अभी अनलॉक करें
                  </span>
                </>
              )}
            </button>

            <p className="mt-2.5 text-center text-[11px] leading-tight text-[#101B2D]/55">
              Pay once · never expires · secured by Razorpay
              <span lang="hi" className="block text-[10px] text-[#101B2D]/40">
                एक बार भुगतान · कभी खत्म नहीं · Razorpay से सुरक्षित
              </span>
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-[#101B2D]/75">
              {!planLoaded ? 'Checking available plans…' : `Pick a plan ${copy.pitch}`}
            </p>
            <a
              href="/pricing"
              className="mt-4 flex h-12 w-full items-center justify-center rounded-xl bg-[#F0A020] text-sm font-bold text-[#101B2D]"
            >
              View plans
            </a>
          </>
        )}

        <button
          onClick={onDismiss}
          disabled={busy}
          className="mt-2 h-9 w-full text-xs font-medium text-[#101B2D]/50 transition-colors hover:text-[#101B2D] disabled:opacity-50"
        >
          {dismissLabel}
        </button>
      </div>

      <LoginRequiredModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </div>
  );
}
